/**
 * 公众号JSAPI支付工具
 * 通过 WeixinJSBridge.invoke('getBrandWCPayRequest', ...) 调起微信收银台
 *
 * ⚠️ 注意：
 * - 仅在微信内置浏览器中有效（无需安装任何SDK，WeixinJSBridge是微信原生注入对象）
 * - 前端回调不可靠（用户可能关闭页面），最终支付结果必须由后端查单确认
 */

import type { JsapiPayParams } from "@/api/wechatPay";

// ==================== 类型定义 ====================

export interface JsapiPaySuccess {
  errMsg: "ok";
  res: WeixinJSBridge.InvokeResult;
}

export interface JsapiPayFail {
  errMsg: string;
  res?: WeixinJSBridge.InvokeResult;
}

// ==================== 工具方法 ====================

/** 判断当前是否在微信浏览器中 */
export function isWeixin(): boolean {
  return /micromessenger/i.test(navigator.userAgent);
}

/**
 * 调起公众号JSAPI支付（WeixinJSBridge）
 *
 * @param payParams 后端返回的6个支付参数
 * @returns Promise<JsapiPaySuccess> 支付成功时 resolve
 * @throws JsapiPayFail 用户取消/失败时 reject
 *
 * @example
 * ```ts
 * import { jsapiCreateOrder, jsapiPay } from "@/utils/wechatPay";
 *
 * // 1. 创建订单获取支付参数
 * const { data } = await jsapiCreateOrder("CR202604241640135064");
 *
 * // 2. 调起微信收银台
 * try {
 *   await invokeWxJsapiPay(data);
 *   // 前端显示成功提示（最终结果需后端确认）
 * } catch (e) {
 *   if (e.errMsg === "用户取消支付") {
 *     // 处理取消
 *   }
 * }
 * ```
 */
export function invokeWxJsapiPay(payParams: JsapiPayParams): Promise<JsapiPaySuccess> {
  return new Promise((resolve, reject) => {
    // 非微信环境直接拒绝
    if (!isWeixin()) {
      reject({ errMsg: "请在微信浏览器中打开" });
      return;
    }

    const doInvoke = () => {
      (window as any).WeixinJSBridge.invoke(
        "getBrandWCPayRequest",
        {
          appId: payParams.appId,
          timeStamp: String(payParams.timeStamp),
          nonceStr: payParams.nonceStr,
          package: payParams.package,
          signType: payParams.signType,
          paySign: payParams.paySign,
        },
        function (res: WeixinJSBridge.InvokeResult) {
          switch (res.err_msg) {
            case "get_brand_wcpay_request:ok":
              // ✅ 前端回调仅作参考，必须后端查询订单确认！
              resolve({ errMsg: "ok", res });
              break;
            case "get_brand_wcpay_request:cancel":
              reject({ errMsg: "用户取消支付", res });
              break;
            case "get_brand_wcpay_request:fail":
            default:
              reject({
                errMsg: (res as any).err_msg || "支付失败",
                res,
              });
              break;
          }
        },
      );
    };

    // WeixinJSBridge 可能还未注入，需等待就绪事件
    if (typeof (window as any).WeixinJSBridge === "undefined") {
      document.addEventListener(
        "WeixinJSBridgeReady",
        () => doInvoke(),
        false,
      );
    } else {
      doInvoke();
    }
  });
}
